import s from './child-profile-settings.module.scss'
import { useState } from 'react'
import classNames from 'classnames'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { format } from 'date-fns'
import { Button } from '@shared/ui/button/button'
import { Input } from '@shared/ui/input/input'
import { CalendarInput } from '@shared/ui/calendar-input/calendar-input'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'
import { useAllInstruments } from '@shared/api/useAllInstruments'
import { useActiveProfileInfo } from '@entities/profile/lib/use-active-profile-info'
import { useUpdateFamilyProfile } from '@entities/profile/api/useUpdateFamilyProfile'
import { getCDNPath } from '@utils/lib/getCDNpath'
import { getAvatarUrl } from '@utils/lib/getAvatarUrl'
import { flattenInstruments } from '@utils/lib/instruments'

// Пресетные аватары для детских профилей (как в children-settings)
const PRESET_AVATARS = [
	'images/avatars/avatar-sax.webp',
	'images/avatars/avatar-cello.webp',
	'images/avatars/avatar-violin-1.webp',
	'images/avatars/avatar-flute.webp',
	'images/avatars/avatar-violin-2.webp',
].map(getCDNPath)

// Данные активного профиля берём из снапшота: список профилей доступен
// только владельцу аккаунта, ребёнок читает свои данные из localStorage.
export const ChildProfileSettings = () => {
	const { activeProfile, setActiveProfile } = useActiveProfileInfo()
	const { data: instrumentGroups = [] } = useAllInstruments()
	const instruments = flattenInstruments(instrumentGroups)
	const updateProfile = useUpdateFamilyProfile()

	const methods = useForm({
		mode: 'onTouched',
		defaultValues: {
			name: activeProfile?.name ?? '',
			bday: activeProfile?.bday ?? '',
		},
	})

	const [selectedAvatar, setSelectedAvatar] = useState(() =>
		activeProfile?.avatar ? getAvatarUrl(activeProfile.avatar) : PRESET_AVATARS[0]
	)
	const [selectedInstruments, setSelectedInstruments] = useState(
		activeProfile?.instruments?.map((instrument) => instrument.id) ?? []
	)

	if (!activeProfile) {
		return null
	}

	const name = methods.watch('name')
	const bday = methods.watch('bday')
	const isValid = name?.trim().length >= 2 && !!bday
	const isPending = updateProfile.isPending

	const toggleInstrument = (id) => {
		setSelectedInstruments((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
		)
	}

	const submit = methods.handleSubmit((data) => {
		const payload = {
			name: data.name.trim(),
			bday: data.bday,
			instruments: selectedInstruments,
			avatar: selectedAvatar,
		}

		updateProfile.mutateAsync({ id: activeProfile.id, ...payload }).then(() => {
			// Обновляем снапшот активного профиля (переключатель, заголовки)
			setActiveProfile({
				...activeProfile,
				...payload,
				is_child: true,
				instruments: instruments.filter((instrument) =>
					selectedInstruments.includes(instrument.id)
				),
			})
		})
	})

	return (
		<FormProvider {...methods}>
			<form className={s.card} onSubmit={submit}>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Аватар</h3>
					<div className={s.avatars}>
						{PRESET_AVATARS.map((avatar) => (
							<button
								key={avatar}
								type="button"
								className={classNames(s.avatarOption, selectedAvatar === avatar && s.avatarActive)}
								onClick={() => setSelectedAvatar(avatar)}
								aria-label="Выбрать аватар"
							>
								<img src={avatar} alt="" />
							</button>
						))}
					</div>
				</div>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Данные</h3>
					<div className={s.fields}>
						<Input
							name="name"
							placeholder="Иван"
							className={s.input}
							validation={{
								required: 'Укажите имя',
								minLength: { value: 2, message: 'Минимум 2 символа' },
							}}
						/>
						<Controller
							name="bday"
							control={methods.control}
							rules={{ required: 'Укажите дату рождения' }}
							render={({ field }) => (
								<CalendarInput
									placeholder="ДД.ММ.ГГГГ"
									value={field.value}
									onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
								/>
							)}
						/>
					</div>
				</div>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Инструменты</h3>
					<div className={s.instruments}>
						{instruments.map((instrument) => (
							<button
								key={instrument.id}
								type="button"
								className={classNames(
									s.instrumentChip,
									selectedInstruments.includes(instrument.id) && s.instrumentActive
								)}
								onClick={() => toggleInstrument(instrument.id)}
							>
								<TextWithEmoji text={instrument.name} />
							</button>
						))}
					</div>
				</div>
				<div className={s.footer}>
					<Button label="Сохранить" disabled={!isValid} fetching={isPending} />
				</div>
			</form>
		</FormProvider>
	)
}
